import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomRouteMatch } from './useCustomRouteMatch';
import { NAMESPACES } from '../constants/namespaces';
import {
  ROOT,
  SEARCH,
  RULES,
  COMMON_ERROR,
  GAME,
  BONUSES,
  BONUS_HISTORY,
  BONUS_PAGE,
  BONUSES_WAGERING_GAMES,
  BONUSES_WAGERING_GAMES_SCCOM,
  BONUSES_COLLECTING_GAMES,
  DEMO_GAME,
  CATEGORY_DETAIL,
  PARAM_NAME,
  RULES_DETAIL,
  SPECIAL_OFFER_PAGE,
  SPECIAL_OFFERS,
  HALL_OF_FAME,
  HALL_OF_FAME_HISTORY,
  CATEGORY_DETAIL_PAGINATED,
  CATEGORY_LIST,
  PROVIDER_LIST,
  PROVIDER_DETAIL,
  PROVIDER_DETAIL_PAGINATED,
} from '../constants/routeTemplates';
import { useQueryGameInfo } from './API/useQueryGames';
import { useQueryCategory } from './API/useQueryCategories';
import { useQueryRulesDetails } from './API/useQueryRules';
import { useQueryProvider } from './API/useQueryProviders';
import { useSelector } from 'react-redux';
import { getBonusItemDataSelector } from '../redux/reducers/domain/bonusContent';
import { RECENT_CATEGORY } from '../constants/navigation';
import { NAMESPACE_SEPARATOR } from '../constants';
import { useIsSC } from './useIsSC';
import { useIsDesktopLayout } from './useMediaQuery';

const getMainPageTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Main`);
const getRulesPageTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Rules`);
const getRulesDetailsPageTitle = ({ translationKey }) => translationKey;
const getSearchPageTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Search`);
const getBonusesPageTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Bonuses`);

const getHallOfFamePageTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Hall of fame`);
const getSpecialOffersTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Special offers`);
const getSpecialOffersItemTitle = ({ translationKey }) => translationKey;
const getGameTitle = ({ translationKey }) => translationKey;
const getCategoryListTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Categories`);
const getCategoryDetailTitle = ({ translationKey, translate }) =>
  translate(`${NAMESPACES.CATEGORY}${NAMESPACE_SEPARATOR}${translationKey}`);

const getProviderListTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Providers`);
const getProviderDetailTitle = ({ translationKey, translate }) =>
  translate(`${NAMESPACES.PROVIDER}${NAMESPACE_SEPARATOR}${translationKey}`);

const getBonusGamesWageringTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Games for wagering`);
const getBonusGamesCollectingTitle = ({ translate }) =>
  translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Games for collecting`);
const getCommonErrorCode = ({ params }) => params.status;

const CONFIG = {
  [ROOT]: getMainPageTitle,
  [CATEGORY_LIST]: getCategoryListTitle,
  [CATEGORY_DETAIL]: getCategoryDetailTitle,
  [CATEGORY_DETAIL_PAGINATED]: getCategoryDetailTitle,
  [PROVIDER_LIST]: getProviderListTitle,
  [PROVIDER_DETAIL]: getProviderDetailTitle,
  [PROVIDER_DETAIL_PAGINATED]: getProviderDetailTitle,
  [RULES]: getRulesPageTitle,
  [SPECIAL_OFFERS]: getSpecialOffersTitle,
  [SPECIAL_OFFER_PAGE]: getSpecialOffersItemTitle,
  [RULES_DETAIL]: getRulesDetailsPageTitle,
  [SEARCH]: getSearchPageTitle,
  [BONUSES]: getBonusesPageTitle,
  [BONUS_PAGE]: getBonusesPageTitle,
  [BONUS_HISTORY]: getBonusesPageTitle,
  [BONUSES_WAGERING_GAMES]: getBonusGamesWageringTitle,
  [BONUSES_WAGERING_GAMES_SCCOM]: getBonusGamesWageringTitle,
  [BONUSES_COLLECTING_GAMES]: getBonusGamesCollectingTitle,
  [GAME]: getGameTitle,
  [DEMO_GAME]: getGameTitle,
  [COMMON_ERROR]: getCommonErrorCode,
  // Hall of fame pages
  [HALL_OF_FAME]: getHallOfFamePageTitle,
  [HALL_OF_FAME_HISTORY]: getHallOfFamePageTitle,
};

const getDynamicPageTitle = (path, configMap = {}) => {
  if ([GAME, DEMO_GAME].includes(path)) {
    return configMap?.[path]?.data?.game?.translationKey ?? '';
  }

  if ([RULES_DETAIL].includes(path)) {
    return configMap?.[path]?.data?.name ?? '';
  }

  if ([SPECIAL_OFFER_PAGE].includes(path)) {
    return configMap?.[path] ?? '';
  }

  return configMap?.[path]?.data?.translationKey ?? '';
};

const REQUIRED_NAMESPACES = [NAMESPACES.PAGES, NAMESPACES.CATEGORY];

export const useTranslate = (namespace = NAMESPACES.PAGES) => {
  const { t, ready } = useTranslation(namespace, {
    useSuspense: false,
  });

  // Return empty string for title if translations is not ready to hide translation keys from the user
  return useMemo(() => (ready ? t : () => ''), [ready, t]);
};

export const usePageTitle = () => {
  const { path, params } = useCustomRouteMatch();
  const translate = useTranslate(REQUIRED_NAMESPACES);
  const isDesktop = useIsDesktopLayout();
  const isSCCOM = useIsSC();

  const gameId = params?.[PARAM_NAME.GAME];
  const categoryId = params?.[PARAM_NAME.CATEGORY];
  const providerId = params?.[PARAM_NAME.PROVIDER];
  const rulesId = params?.[PARAM_NAME.RULES];
  const offerId = params?.[PARAM_NAME.OFFER];

  const queryGame = useQueryGameInfo(gameId, {
    enabled: path === GAME && !!gameId,
  });

  const queryCategory = useQueryCategory(categoryId, {
    enabled:
      [CATEGORY_DETAIL, CATEGORY_DETAIL_PAGINATED].includes(path) &&
      !!categoryId &&
      RECENT_CATEGORY.id !== categoryId,
  });

  const queryProviders = useQueryProvider(providerId, {
    enabled: [PROVIDER_DETAIL, PROVIDER_DETAIL_PAGINATED].includes(path),
  });

  const queryRule = useQueryRulesDetails(rulesId, {
    enabled: path === RULES_DETAIL && !!rulesId,
  });

  const specialOffer = useSelector((state) =>
    getBonusItemDataSelector(state, offerId)
  );

  const queryConfigMap = {
    [GAME]: queryGame,
    [DEMO_GAME]: queryGame,
    [CATEGORY_DETAIL]: queryCategory,
    [CATEGORY_DETAIL_PAGINATED]: queryCategory,
    [PROVIDER_DETAIL]: queryProviders,
    [PROVIDER_DETAIL_PAGINATED]: queryProviders,
    [RULES_DETAIL]: queryRule,
    [SPECIAL_OFFER_PAGE]: specialOffer?.title,
  };

  const translationKey = getDynamicPageTitle(path, queryConfigMap);

  if (RECENT_CATEGORY.id === categoryId) {
    if (isSCCOM && isDesktop) {
      return '';
    }

    return translate(RECENT_CATEGORY.translationKey);
  }

  return CONFIG[path]
    ? CONFIG[path]({ translate, translationKey, params })
    : '';
};
